/**
 * SOLID Review
 * S — Strong. One concern: first-party persistence of a manual cookie-consent choice — load, save, clear — with its versioned
 *     schema validation.
 * O — Good. A schema-version bump (CURRENT_VERSION) is the extension point for a new payload shape; `load` rejects a mismatched
 *     version or a malformed payload rather than trusting it.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Three methods.
 * D — Strong. The browser's storage area and the clock are handed in; the domain's categories type only.
 */
import type { IEntityCookieConsentCategories } from '../../domain/ports/IStateCookieConsent';
import type { IServiceBrowserStorage } from '../storage/IServiceBrowserStorage';
import type { IServiceClock } from '../platform/IServiceClock';

const STORAGE_KEY = 'meyer_cookie_consent';
const CURRENT_VERSION = 1;

interface PersistedConsent {
  v: number;
  categories: IEntityCookieConsentCategories;
  ts: number;
}

/**
 * A MANUAL COOKIE-CONSENT CHOICE, KEPT (frontend-infrastructure-oo P1, 2026-10-28, §28.286) — `consentStorage.ts`'s functions as
 * one object over the browser's shared storage area, built once by the composition root and handed to `ConsentGateway`.
 * Behaviour unchanged.
 *
 * The certified CMP (Google Funding Choices) persists its own decision in the `FCCDCF` cookie / TCF string, but choices made in
 * our first-party preferences modal were once only held in memory — so "Accept all" / "Reject all" were forgotten on the next
 * load. The manual choice is mirrored here and restored on the gateway's init, so it survives a reload.
 */
export class ManualConsentStore {
  constructor(
    private readonly local: IServiceBrowserStorage,
    /** When a choice was made — the record's `ts` (frontend-infrastructure-oo P4, §28.289). */
    private readonly clock: IServiceClock,
  ) {}

  load(): IEntityCookieConsentCategories | null {
    try {
      const raw = this.local.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<PersistedConsent> | null;
      if (!parsed || parsed.v !== CURRENT_VERSION || !parsed.categories) return null;
      const c = parsed.categories;
      // Reject malformed payloads (e.g. an older/foreign schema) rather than trust them.
      if (typeof c.adStorage !== 'boolean' || typeof c.functionality !== 'boolean') return null;
      return {
        adStorage: !!c.adStorage,
        adUserData: !!c.adUserData,
        adPersonalization: !!c.adPersonalization,
        analyticsStorage: !!c.analyticsStorage,
        functionality: true, // strictly-necessary is always on
      };
    } catch {
      return null;
    }
  }

  save(categories: IEntityCookieConsentCategories): void {
    try {
      const payload: PersistedConsent = { v: CURRENT_VERSION, categories, ts: this.clock.now() };
      this.local.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Storage unavailable / quota exceeded — degrade to in-memory only.
    }
  }

  clear(): void {
    try {
      this.local.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}
